import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import StoreCatalog from '../../components/mobile/StoreCatalog';
import DashboardModal from '../../components/actions/DashboardModal/DashboardModal';
import MobileNotificationsPanel from '../../components/mobile/MobileNotificationsPanel';
import MobileTenantInvoicePanel from '../../components/mobile/MobileTenantInvoicePanel';
import MobileTenantContractPanel from '../../components/mobile/MobileTenantContractPanel';
import MobileCancelContractControl from '../../components/mobile/MobileCancelContractControl';
import MobileSettingsControl from '../../components/mobile/MobileSettingsControl';
import MobileCornerAd from '../../components/mobile/MobileCornerAd';
import MobileSupportFab from '../../components/mobile/MobileSupportFab';
import AddContractModal, { type ContractFormPreset } from '../../components/AddContractModal';
import { ProfileType } from '../../utils/profileType';
import { getLastMobileTab, hasPersistedTenantMobileSession, MOBILE_APP_LOGIN_PATH, setLastMobileTab } from '../../utils/mobileApp';
import kcnBellIcon from '../../assets/icons/kcn-bell.svg';
import { getToken } from '../../services/authApi';
import MobileManagePanel from '../../components/mobile/MobileManagePanel';
import {
  askAndroid13NotificationPermission,
  attachMobilePushListeners,
  clearFcmRegisterCrashGuard,
  didFcmRegisterCrashLastTime,
  initMobilePushNotifications,
  isNativeNotificationPermissionRequired,
  isMobilePushEnabled,
  setMobilePushEnabled,
  unregisterMobilePushToken,
} from '../../utils/mobilePushNotifications';
import {
  CONTRACT_REQUEST_EVENT,
  contractPresetFromRequestMetadata,
  getContractRequestMetadata,
} from '../../utils/contractRequestNotification';
import { NotificationStatus } from '../../services/notificationApi';
import { TENANT_OPEN_INVOICE_EVENT } from '../../utils/tenantNotifications';
import { getMobileChromeTheme, setMobileChromeTheme, type MobileChromeTheme } from '../../utils/mobileTheme';

type MobileTab = 'shop' | 'notifications' | 'stats' | 'manage' | 'invoice' | 'contract';

const MobileStatsPanel = memo(function MobileStatsPanel({ profileType }: { profileType: ProfileType }) {
  return (
    <section>
      <DashboardModal profileType={profileType} mode="inline" />
    </section>
  );
});

const landlordTabs: { id: MobileTab; label: string; icon: string; external?: boolean }[] = [
  { id: 'stats', label: 'Thống kê', icon: 'fa-chart-pie' },
  { id: 'manage', label: 'Quản lý', icon: 'fa-sliders-h' },
  { id: 'notifications', label: 'Thông báo', icon: 'fa-bell' },
  { id: 'shop', label: 'Cửa hàng', icon: 'fa-store' },
];

const tenantTabs: { id: MobileTab; label: string; icon: string; external?: boolean }[] = [
  { id: 'invoice', label: 'Hóa đơn', icon: 'fa-file-invoice-dollar' },
  { id: 'contract', label: 'Hợp đồng', icon: 'fa-file-contract' },
  { id: 'notifications', label: 'Thông báo', icon: 'fa-bell' },
  { id: 'shop', label: 'Cửa hàng', icon: 'fa-store' },
];

/**
 * Shell app mobile (APK): chủ nhà 4 tab; khách thuê Hóa đơn + Hợp đồng + Thông báo + Cửa hàng.
 */
const MobileAppPage: React.FC = () => {
  const { user, logout, isLoading, invalidateTenantSession } = useAuth();
  const { unreadCount, notifications, markAsRead, fetchNotifications } = useNotifications();
  const [activeTab, setActiveTab] = useState<MobileTab>(() => {
    const last = getLastMobileTab();
    if (last === 'manage' || last === 'stats' || last === 'notifications' || last === 'shop') {
      return last;
    }
    return 'stats';
  });
  const [visitedTabs, setVisitedTabs] = useState<Set<MobileTab>>(() => {
    const last = getLastMobileTab();
    if (last === 'manage' || last === 'notifications' || last === 'shop') {
      return new Set(['stats', last]);
    }
    return new Set(['stats']);
  });
  const [pushOn, setPushOn] = useState(true);
  const [pushBusy, setPushBusy] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptPreset, setAcceptPreset] = useState<ContractFormPreset | null>(null);
  const [acceptTitle, setAcceptTitle] = useState<string>();
  const [acceptNotificationId, setAcceptNotificationId] = useState<number | null>(null);
  const [unpaidInvoice, setUnpaidInvoice] = useState(false);
  const [chromeTheme, setChromeTheme] = useState<MobileChromeTheme>(() => getMobileChromeTheme());
  const isLightChrome = chromeTheme === 'light';
  const shownContractRequestIdsRef = useRef<Set<number>>(new Set());
  const lastSessionVerifyAt = useRef(0);
  const lastNotifRefreshAt = useRef(0);

  const profileType = (user?.accountType || 'landlord') as ProfileType;
  const isTenantApp = profileType === 'tenant';
  const tabs = isTenantApp ? tenantTabs : landlordTabs;

  const openAcceptFromMetadata = useCallback((detail: Record<string, unknown>, notificationId?: number) => {
    if (profileType === 'tenant') return;
    const parsed = contractPresetFromRequestMetadata(detail);
    if (!parsed) return;

    const id =
      notificationId ??
      (typeof detail.notification_id === 'number'
        ? detail.notification_id
        : Number(detail.notification_id) || undefined);
    const key = id && Number.isFinite(id) ? id : null;

    setAcceptPreset(parsed.preset);
    setAcceptTitle(parsed.title);
    setAcceptNotificationId(key);
    setAcceptOpen(true);
    if (key) {
      void markAsRead(key);
    }
  }, [markAsRead, profileType]);

  useEffect(() => {
    void attachMobilePushListeners();
  }, []);

  useEffect(() => {
    const onContractRequest = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail;
      if (!detail) return;
      const notificationId =
        typeof detail.notification_id === 'number' ? detail.notification_id : Number(detail.notification_id) || undefined;
      if (notificationId) {
        shownContractRequestIdsRef.current.add(notificationId);
      }
      openAcceptFromMetadata(detail, notificationId);
    };

    window.addEventListener(CONTRACT_REQUEST_EVENT, onContractRequest);
    return () => {
      window.removeEventListener(CONTRACT_REQUEST_EVENT, onContractRequest);
    };
  }, [openAcceptFromMetadata]);

  useEffect(() => {
    if (!isTenantApp) return;
    setActiveTab('invoice');
    setVisitedTabs(new Set(['invoice']));
  }, [isTenantApp]);

  useEffect(() => {
    if (!isTenantApp) return;
    const openInvoice = () => {
      setActiveTab('invoice');
      setVisitedTabs((prev) => {
        const next = new Set(prev);
        next.add('invoice');
        return next;
      });
    };
    window.addEventListener(TENANT_OPEN_INVOICE_EVENT, openInvoice);
    return () => window.removeEventListener(TENANT_OPEN_INVOICE_EVENT, openInvoice);
  }, [isTenantApp]);

  useEffect(() => {
    if (!isTenantApp || isLoading || !user?.id || !hasPersistedTenantMobileSession()) return;

    const verifyTenantSession = async () => {
      try {
        const { checkTenantQrSession } = await import('../../services/tenantQrAuthApi');
        const status = await checkTenantQrSession();
        if (!status.valid) {
          await invalidateTenantSession(
            status.message ||
              'Phiên đăng nhập đã hết hạn. Vui lòng nhập số căn cước công dân lại.',
          );
        }
      } catch {
        // Bỏ qua lỗi mạng
      }
    };

    void verifyTenantSession();
    lastSessionVerifyAt.current = Date.now();

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastSessionVerifyAt.current < 60_000) return;
      lastSessionVerifyAt.current = now;
      void verifyTenantSession();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [invalidateTenantSession, isLoading, isTenantApp, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    void fetchNotifications();
    lastNotifRefreshAt.current = Date.now();
  }, [user?.id, fetchNotifications]);

  useEffect(() => {
    if (profileType === 'tenant' || acceptOpen) return;

    const pending = notifications.find((notification) => {
      if (shownContractRequestIdsRef.current.has(notification.id)) return false;
      if (notification.status === NotificationStatus.READ) return false;
      return Boolean(getContractRequestMetadata(notification));
    });
    if (!pending) return;

    const metadata = getContractRequestMetadata(pending);
    if (!metadata) return;

    shownContractRequestIdsRef.current.add(pending.id);
    openAcceptFromMetadata(metadata, pending.id);
  }, [acceptOpen, notifications, openAcceptFromMetadata, profileType]);

  useEffect(() => {
    const refreshRequests = () => {
      void fetchNotifications();
    };
    window.addEventListener('kcn:refresh-notifications', refreshRequests);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastNotifRefreshAt.current < 45_000) return;
      lastNotifRefreshAt.current = now;
      refreshRequests();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('kcn:refresh-notifications', refreshRequests);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    if (!user?.id) return;
    if (didFcmRegisterCrashLastTime()) {
      setMobilePushEnabled(user.id, false);
      setPushOn(false);
      return;
    }
    const enabled = isMobilePushEnabled(user.id);
    setPushOn(enabled);
    if (!enabled) return;
    setMobilePushEnabled(user.id, true);
    const timer = window.setTimeout(() => {
      void (async () => {
        if (isNativeNotificationPermissionRequired()) {
          const permission = await askAndroid13NotificationPermission();
          if (permission === 'denied') {
            setMobilePushEnabled(user.id, false);
            setPushOn(false);
            return;
          }
        }
        const ok = await initMobilePushNotifications(user.id, { requestPermission: false });
        if (!ok && didFcmRegisterCrashLastTime()) {
          setMobilePushEnabled(user.id, false);
          setPushOn(false);
        }
      })();
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [user?.id]);

  const handleTogglePush = async () => {
    if (!user?.id || pushBusy) return;
    setPushBusy(true);
    if (pushOn) {
      await unregisterMobilePushToken();
      setMobilePushEnabled(user.id, false);
      setPushOn(false);
      setPushBusy(false);
      return;
    }

    if (isNativeNotificationPermissionRequired()) {
      const permission = await askAndroid13NotificationPermission();
      if (permission !== 'granted') {
        setMobilePushEnabled(user.id, false);
        setPushOn(false);
        setPushBusy(false);
        return;
      }
    }

    clearFcmRegisterCrashGuard();
    setMobilePushEnabled(user.id, true);
    setPushOn(true);
    setPushBusy(false);

    window.setTimeout(() => {
      void initMobilePushNotifications(user.id, { requestPermission: false }).then((ok) => {
        if (!ok) {
          setMobilePushEnabled(user.id, false);
          setPushOn(false);
        }
      });
    }, 4000);
  };

  const handleTabClick = (tab: (typeof tabs)[number]) => {
    setVisitedTabs((prev) => {
      if (prev.has(tab.id)) return prev;
      const next = new Set(prev);
      next.add(tab.id);
      return next;
    });
    setActiveTab(tab.id);
    setLastMobileTab(tab.id);
  };

  const toggleChromeTheme = () => {
    const next: MobileChromeTheme = isLightChrome ? 'gold' : 'light';
    setChromeTheme(next);
    setMobileChromeTheme(next);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(165deg,#F7E6C4_0%,#EED6A4_48%,#E2C47A_100%)]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#8C5226] border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    if (getToken()) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(165deg,#F7E6C4_0%,#EED6A4_48%,#E2C47A_100%)]">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#8C5226] border-t-transparent" />
        </div>
      );
    }
    return <Navigate to={MOBILE_APP_LOGIN_PATH} replace />;
  }

  return (
    <div className={`kcn-native-shell relative flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden overscroll-none ${isLightChrome ? 'kcn-theme-light' : ''}`}>
      <header className="kcn-native-header shrink-0 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#C4A35A] shadow-[0_0_0_3px_rgba(216,184,106,0.45)]">
              <img
                src={kcnBellIcon}
                alt="KCN"
                className="h-7 w-7 object-contain"
              />
            </div>
            <div className="min-w-0">
              <p className={`truncate text-sm font-bold ${isLightChrome ? 'text-black' : 'text-white'}`}>{user.name || user.email}</p>
              {!isTenantApp && <p className={`text-xs font-semibold ${isLightChrome ? 'text-black/50' : 'text-[#E4C078]'}`}>KCN Landlord</p>}
              {isTenantApp && <p className={`text-xs font-semibold ${isLightChrome ? 'text-black/50' : 'text-[#E4C078]'}`}>KCN Khách thuê</p>}
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-end gap-2">
            <button
              type="button"
              onClick={toggleChromeTheme}
              className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#C4A35A] text-white"
              title={isLightChrome ? 'Khung tối' : 'Khung sáng'}
              aria-label={isLightChrome ? 'Chuyển khung tối' : 'Chuyển khung sáng'}
            >
              <i className={`fas ${isLightChrome ? 'fa-moon' : 'fa-sun'} text-sm`} />
            </button>
            <MobileCancelContractControl
              isTenant={isTenantApp}
              trailing={
                <MobileSettingsControl
                  pushOn={pushOn}
                  pushBusy={pushBusy}
                  onTogglePush={() => void handleTogglePush()}
                  onLogout={logout}
                />
              }
            />
          </div>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto overscroll-none px-3 py-4 sm:px-4">
        {visitedTabs.has('shop') && (
          <section className={`rounded-2xl border border-black/10 bg-white p-3 shadow-sm sm:p-4 ${activeTab === 'shop' ? '' : 'hidden'}`}>
            <div className="mb-3">
              <h1 className="text-lg font-semibold text-black">Cửa hàng</h1>
            </div>
            <StoreCatalog />
          </section>
        )}

        {isTenantApp && (
          <div className={activeTab === 'invoice' ? '' : 'hidden'}>
            <MobileTenantInvoicePanel onUnpaidChange={setUnpaidInvoice} />
          </div>
        )}

        {isTenantApp && visitedTabs.has('contract') && (
          <div className={activeTab === 'contract' ? '' : 'hidden'}>
            <MobileTenantContractPanel active={activeTab === 'contract'} />
          </div>
        )}

        {visitedTabs.has('notifications') && (
          <div className={activeTab === 'notifications' ? '' : 'hidden'}>
            <MobileNotificationsPanel />
          </div>
        )}

        {visitedTabs.has('stats') && !isTenantApp && (
          <div className={activeTab === 'stats' ? '' : 'hidden'}>
            <MobileStatsPanel profileType={profileType} />
          </div>
        )}

        {visitedTabs.has('manage') && !isTenantApp && (
          <div className={activeTab === 'manage' ? '' : 'hidden'}>
            <MobileManagePanel profileType={profileType} />
          </div>
        )}
      </main>

      <nav className="kcn-native-nav shrink-0 pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-4 gap-1 px-1.5 py-1.5">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const unreadBadge = tab.id === 'notifications' && unreadCount > 0 ? unreadCount : 0;
            const invoiceDot = tab.id === 'invoice' && unpaidInvoice;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabClick(tab)}
                className={`relative flex flex-col items-center justify-center gap-0.5 rounded-2xl px-1 py-2 text-[10px] font-semibold transition-all ${
                  isActive
                    ? 'bg-[#C4A35A] text-white shadow-[0_6px_14px_rgba(196,163,90,0.45)]'
                    : isLightChrome
                      ? 'text-black/50'
                      : 'text-[#F8EFD8]/75'
                }`}
              >
                <span className="relative">
                  <i className={`fas ${tab.icon} text-base`} />
                  {invoiceDot && (
                    <span className="absolute -right-1 -top-0.5 h-2 w-2 rounded-full bg-red-400" />
                  )}
                  {unreadBadge > 0 && (
                    <span className="absolute -right-2 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {unreadBadge > 99 ? '99+' : unreadBadge}
                    </span>
                  )}
                </span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {!isTenantApp && (
      <AddContractModal
        isOpen={acceptOpen}
        onClose={() => {
          setAcceptOpen(false);
          setAcceptPreset(null);
          setAcceptTitle(undefined);
          setAcceptNotificationId(null);
        }}
        mode="accept"
        propertyId={acceptPreset?.property_id}
        propertyTitle={acceptTitle}
        propertyPrice={acceptPreset?.monthly_payment}
        initialData={acceptPreset ?? undefined}
        onContractAdded={() => {
          if (acceptNotificationId) {
            void markAsRead(acceptNotificationId);
          }
          setAcceptOpen(false);
          setAcceptPreset(null);
          setAcceptTitle(undefined);
          setAcceptNotificationId(null);
          void fetchNotifications();
        }}
      />
      )}
      <div className="absolute right-3 z-[61] bottom-[calc(4.75rem+env(safe-area-inset-bottom))]">
        <MobileSupportFab />
      </div>
      <MobileCornerAd />
    </div>
  );
};

export default MobileAppPage;
